// src/components/panels/RequirementsPanel.jsx
import React, { useState } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { createRequirement, deleteRequirement } from '../../api/requirementApi';
import { toast } from "react-toastify";
import RequirementsList from "../programDirector/RequirementList";
import DeleteConfirmationModal from "../programDirector/DeleteConfirmationModal";

const COMMON_UNITS = [
    "hour(s)", "day(s)", "week(s)", "month(s)", "person(s)", "team(s)", "piece(s)", "unit(s)", "item(s)", "set(s)", "package(s)", "lot(s)", "kg(s)", "g(s)", "ton(s)", "liter(s)", "ml(s)", "m³(s)", "meter(s)", "cm(s)", "km(s)", "ft(s)", "yd(s)", "m²(s)", "event(s)", "trip(s)"
];

const BUDGET_CODES = [
    "01-Staffing", "02-Materials", "03-Services", "04-Travel"
];

const RequirementsPanel = ({ projectId, requirements, onUpdate }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newReq, setNewReq] = useState({
        name: '',
        type: 'STAFF',
        quantity: 1,
        unit: '',
        unitCost: 0,
        budgetCode: '',
        neededBy: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [requirementToDelete, setRequirementToDelete] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewReq(prev => {
            const updatedReq = { ...prev, [name]: value };
            if (name === 'quantity' || name === 'unitCost') {
                const quantity = parseFloat(updatedReq.quantity) || 0;
                const unitCost = parseFloat(updatedReq.unitCost) || 0;
                updatedReq.totalCost = quantity * unitCost;
            }
            return updatedReq;
        });
    };

    const handleAddRequirement = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        const { name, type, quantity, unit, unitCost, budgetCode, neededBy } = newReq;
        if (!name || !type || !quantity || !unit || !unitCost || !budgetCode || !neededBy) {
            toast.error("Please fill in all required fields.");
            setIsSaving(false);
            return;
        }

        try {
            const requirementToSave = {
                name,
                type,
                quantity: parseFloat(quantity),
                unit,
                unitCost: parseFloat(unitCost),
                budgetCode,
                projectId,
                status: 'Pending',
                neededBy
            };
            await createRequirement(requirementToSave);
            toast.success("Requirement added successfully!");
            setNewReq({
                name: '',
                type: 'STAFF',
                quantity: 1,
                unit: '',
                unitCost: 0,
                budgetCode: '',
                neededBy: ''
            });
            setIsAdding(false);
            onUpdate(); // Trigger a data refresh in the parent component
        } catch (err) {
            toast.error("Failed to add requirement. Please try again.");
            console.error("Error adding requirement:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenDeleteModal = (requirement) => {
        setRequirementToDelete(requirement);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        setIsDeleteModalOpen(false);
        if (!requirementToDelete) return;
        
        try {
            await deleteRequirement(requirementToDelete._id);
            toast.success("Requirement deleted successfully!");
            onUpdate(); // Trigger a data refresh
        } catch (err) {
            toast.error("Failed to delete requirement.");
            console.error("Error deleting requirement:", err);
        } finally {
            setRequirementToDelete(null);
        }
    };

    const cancelDelete = () => {
        setIsDeleteModalOpen(false);
        setRequirementToDelete(null);
    };

    const DUMMY_BUDGET = 500000;
    const totalSpent = requirements.reduce((sum, req) => sum + (req.totalCost || 0), 0);
    const remainingBudget = DUMMY_BUDGET - totalSpent;
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <div className="flex flex-col h-full">
            <div className="bg-orange-100 p-3 rounded-lg mb-4">
                <h3 className="text-md font-semibold text-orange-800 mb-1">Budget Overview</h3>
                <div className="text-orange-700 text-sm">
                    <p>Total: **${DUMMY_BUDGET.toLocaleString()}**</p>
                    <p>Spent: **${totalSpent.toLocaleString()}**</p>
                    <p>Remaining: **${remainingBudget.toLocaleString()}**</p>
                </div>
            </div>

            <button
                onClick={() => setIsAdding(!isAdding)}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition duration-300 shadow-md mb-4"
            >
                <PlusCircle size={20} className="mr-2" /> {isAdding ? "Cancel" : "Add New Requirement"}
            </button>

            {isAdding && (
                <form onSubmit={handleAddRequirement} className="bg-white p-4 rounded-lg shadow-inner mb-4">
                    <h4 className="font-semibold mb-2">New Requirement</h4>
                    <input type="text" name="name" value={newReq.name} onChange={handleInputChange} placeholder="Name" className="w-full mb-2 p-2 border rounded" required />
                    <select name="type" value={newReq.type} onChange={handleInputChange} className="w-full mb-2 p-2 border rounded" required>
                        <option value="STAFF">Staff</option>
                        <option value="ITEM">Item</option>
                        <option value="SERVICE">Service</option>
                    </select>
                    <div className="flex space-x-2 mb-2">
                        <input type="number" name="quantity" value={newReq.quantity} onChange={handleInputChange} min="1" placeholder="Quantity" className="w-1/2 p-2 border rounded" required />
                        <input list="common-units" name="unit" value={newReq.unit} onChange={handleInputChange} placeholder="Unit (e.g., hour, piece)" className="w-1/2 p-2 border rounded" required />
                        <datalist id="common-units">
                            {COMMON_UNITS.map(unit => <option key={unit} value={unit} />)}
                        </datalist>
                    </div>
                    <div className="flex space-x-2 mb-2">
                        <select name="budgetCode" value={newReq.budgetCode} onChange={handleInputChange} className="w-1/2 p-2 border rounded" required>
                            <option value="">Budget Code</option>
                            {BUDGET_CODES.map(code => <option key={code} value={code}>{code}</option>)}
                        </select>
                        <input type="number" name="unitCost" value={newReq.unitCost} onChange={handleInputChange} min="0" step="0.01" placeholder="Unit Cost" className="w-1/2 p-2 border rounded" required />
                    </div>
                    <input type="date" name="neededBy" value={newReq.neededBy} onChange={handleInputChange} min={getTodayDate()} className="w-full mb-2 p-2 border rounded" required />
                    <p className="text-sm text-gray-600 mb-2">Total Cost: **${newReq.totalCost ? newReq.totalCost.toLocaleString() : '0'}**</p>
                    <button type="submit" disabled={isSaving} className="w-full bg-green-500 text-white p-2 rounded hover:bg-green-600 transition">
                        {isSaving ? 'Saving...' : 'Save Requirement'}
                    </button>
                </form>
            )}

            <div className="flex-grow overflow-y-auto pr-2 space-y-3">
                <RequirementsList 
                    requirements={requirements} 
                    onOpenDeleteModal={handleOpenDeleteModal} 
                />
            </div>
            
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={cancelDelete}
                    onConfirm={confirmDelete}
                    itemName={requirementToDelete?.name || ''}
                />
            )}
        </div>
    );
};

export default RequirementsPanel;